import { MonoDatePicker } from 'components/shared/DatePicker';
import { FC, useCallback, useMemo } from 'react';
import type { CpOrderSettlementType } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import type { Settlement } from 'state/secondaryMarket/shared/referential/referential.model';
import { fromSettlementTypeKey } from 'state/secondaryMarket/shared/utils';

import { Block } from '../Blocks/Block';
import { SettlementTypeSelector } from '../components/SettlementTypeSelector';
import { FeedbackMessage } from '../FeedbackMessage';
import { VerticalFormGroup } from '../VerticalFormGroup';

const fieldIds: string[] = ['settlementDate', 'settlementType'];

type BookingInformationProps = {
  tabId: string;
  className?: string;
  settlementDate: string | undefined;
  minSettlementDate?: string | undefined;
  settlementHolidays: ReadonlyArray<string> | undefined;
  availableSettlementTypes: ReadonlyArray<Settlement> | undefined;
  settlementType: string | undefined;
  isLoadingSettlement: boolean;
  isFrozen: boolean;
  onSettlementDateChanged: (value: Nullable<string>) => void;
  onSettlementTypeChanged: (value: Settlement | undefined) => void;
};

export const SecondaryBookingInformation: FC<BookingInformationProps> = ({
  className,
  settlementDate,
  minSettlementDate,
  settlementHolidays,
  availableSettlementTypes,
  isLoadingSettlement,
  settlementType,
  isFrozen,
  onSettlementDateChanged,
  onSettlementTypeChanged,
}) => {
  const blackoutSettlementDates = useMemo<string[]>(() => settlementHolidays?.slice() ?? [], [settlementHolidays]);

  const availableCpOrderSettlements = useMemo<CpOrderSettlementType[]>(
    () => availableSettlementTypes?.map(s => ({ ...s }) as CpOrderSettlementType) ?? [],
    [availableSettlementTypes],
  );

  const onSettlementTypeSelected = useCallback(
    (value: string | undefined) => {
      const key = fromSettlementTypeKey(value);
      const selected = availableSettlementTypes?.find(
        s => s.settlementType === key?.settlementType && s.marketType === key?.marketType,
      );
      onSettlementTypeChanged(selected);
    },
    [availableSettlementTypes, onSettlementTypeChanged],
  );

  const hasIncoherentSettlementDate = settlementDate && blackoutSettlementDates.some(x => x === settlementDate);

  return (
    <Block
      className={className}
      label="secondaryMarket.bookingInformation.label.bookingInformation"
      id="booking-information"
      fieldIds={fieldIds}
    >
      <div>
        <div className="row">
          <VerticalFormGroup
            inputId="settlementDate"
            label="secondaryMarket.bookingInformation.settlementDate"
            className="col-md-6"
          >
            {settlementDate !== undefined && (
              <>
                <MonoDatePicker
                  id="SettlementDate"
                  value={settlementDate}
                  minDate={minSettlementDate}
                  allowWeekend={false}
                  blackoutDates={blackoutSettlementDates}
                  onChange={onSettlementDateChanged}
                  datePresetTranslations={[]}
                  datePresets={[]}
                  isClearable={false}
                  disabled={isFrozen}
                  e2e="settlement-date"
                />
                {hasIncoherentSettlementDate && (
                  <FeedbackMessage labelKey="secondaryMarket.errors.settlementDate.incoherent" color="info" />
                )}
              </>
            )}
          </VerticalFormGroup>
          <VerticalFormGroup
            inputId="settlementType"
            label="secondaryMarket.bookingInformation.settlementType"
            className="col-md-6"
          >
            <SettlementTypeSelector
              isFrozen={isFrozen}
              availableSettlementTypes={availableCpOrderSettlements}
              settlementType={settlementType}
              isLoadingSettlement={isLoadingSettlement}
              onSettlementTypeChanged={onSettlementTypeSelected}
            />
          </VerticalFormGroup>
        </div>
      </div>
    </Block>
  );
};

SecondaryBookingInformation.displayName = 'LiveBookingInformation';
